import React, { useState, useEffect } from "react";
import {
  GoogleMap,
  Circle,
  Marker,
  InfoWindow,
  useLoadScript,
  Polygon,
} from "@react-google-maps/api";
import { Link } from "react-router-dom";
import { AiOutlineZoomIn, AiOutlineZoomOut } from "react-icons/ai";
import { useSelector } from "react-redux";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import SideNav from "../Headers/SideNav/SideNav";
import SensorList from "./SensorList";
import EventList from "./EventList";
import { selectSiteData } from "../reusable_components/site_data/siteDataSlice";
import { backend_url, googleMapsApiKey } from "../App";
import JSZip from "jszip";
import convertXmlToGeoJSON from "../features/events/convertXmlToGeoJSON";
import * as turf from "@turf/turf";
import {
  FaUndo,
  FaFire,
  FaSmog,
  FaInfoCircle,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import "./map.css";

function MapContainer() {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
  });

  const [map, setMap] = useState(null);
  const defaultCenter = { lat: 34.19167584003779, lng: 73.23095807365446 };
  const [center, setCenter] = useState({
    center: defaultCenter,
    zoom: 9,
    isZoom: false,
  });
  const [selected, setSelected] = useState({ theta: 0, item: {} });
  const [circleCenter, setCircleCenter] = useState({
    lat: 34.534508,
    lng: 73.003801,
  });
  const [circleRadius, setCircleRadius] = useState(10000);
  const [fireFootprints, setFireFootprints] = useState(null);
  const { side_nav: side_nav_check } = useSelector(selectSiteData);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState(null);

  // State for cones based on fire/smoke detection
  const [fireCones, setFireCones] = useState([]);
  const [showFireCones, setShowFireCones] = useState(false);
  const [loadingCones, setLoadingCones] = useState(false);

  // State for hovered cone info
  const [hoveredCone, setHoveredCone] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(true);

  // Filter states
  const [filterType, setFilterType] = useState("all");

  const side_nav = side_nav_check ? <SideNav /> : null;
  const mapStyles = {
    height: "90vh",
    width: "100%",
  };

  const handleMapLoad = (map) => {
    setMap(map);
  };

  // Function to calculate angle based on frame index
  const calculateAngleForFrame = (
    frameIndex,
    totalFrames,
    startAngle,
    endAngle,
  ) => {
    if (totalFrames <= 1) return startAngle;
    const angleStep = (endAngle - startAngle) / (totalFrames - 1);
    return startAngle + angleStep * frameIndex;
  };

  // Function to calculate a single cone at a specific angle
  const calculateConeAtAngle = (
    position,
    angleDegrees,
    fovDegrees = 25,
    rangeMeters = 800,
  ) => {
    if (!position || !position.lat || !position.lng)
      return { conePath: [], centerPoint: null };

    const { lat, lng } = position;
    const directionRad = ((angleDegrees - 90) * Math.PI) / 180;
    const halfFovRad = ((fovDegrees / 2) * Math.PI) / 180;
    const leftAngleRad = directionRad - halfFovRad;
    const rightAngleRad = directionRad + halfFovRad;

    const rangeLat = rangeMeters / 111320;
    const rangeLng = rangeMeters / (111320 * Math.cos((lat * Math.PI) / 180));

    const leftPoint = {
      lat: lat + Math.sin(leftAngleRad) * rangeLat,
      lng: lng + Math.cos(leftAngleRad) * rangeLng,
    };

    const rightPoint = {
      lat: lat + Math.sin(rightAngleRad) * rangeLat,
      lng: lng + Math.cos(rightAngleRad) * rangeLng,
    };

    const centerPoint = {
      lat: lat + Math.sin(directionRad) * rangeLat,
      lng: lng + Math.cos(directionRad) * rangeLng,
    };

    return {
      conePath: [{ lat, lng }, leftPoint, rightPoint, { lat, lng }],
      centerPoint,
    };
  };

  // Get cone color based on type
  const getConeColor = (specie, isHovered = false) => {
    if (specie === "fire") {
      return isHovered ? "#FF5555" : "#FF0000";
    }
    return isHovered ? "#FFaa55" : "#FF6600";
  };

  // Filter cones based on selected filter
  const getFilteredCones = () => {
    if (!fireCones.length) return [];
    if (filterType === "fire") {
      return fireCones.filter((cone) => cone.specie === "fire");
    } else if (filterType === "smoke") {
      return fireCones.filter((cone) => cone.specie === "smoke");
    } else if (filterType === "none") {
      return [];
    }
    return fireCones;
  };

  // Fetch fire/smoke events for a camera and create cones
  const fetchFireEventsForCamera = async (camera) => {
    setLoadingCones(true);
    setShowInfoWindow(true);
    setFilterType("all");
    setFireCones([]);
    setShowFireCones(false);

    try {
      console.log("========== FETCHING FOR CAMERA ==========");
      console.log("Camera ID:", camera.id);
      console.log("Camera Description:", camera.description);

      const cameraResponse = await fetch(
        `${backend_url}/core/api/camera/${camera.id}/`,
        {
          headers: { Authorization: `Token ${localStorage.getItem("token")}` },
        },
      );
      const cameraData = await cameraResponse.json();

      const startAngle = cameraData.start_angle || 0;
      const endAngle = cameraData.end_angle || 180;
      const totalFrames = cameraData.video_interval || 15;
      const coneRangeMeters = 800;
      const coneFOV = 25;

      const eventsResponse = await fetch(
        `${backend_url}/core/api/event/?cameras=${camera.id}&page_size=50`,
        {
          headers: { Authorization: `Token ${localStorage.getItem("token")}` },
        },
      );
      const eventsData = await eventsResponse.json();

      let allImages = [];
      for (const event of eventsData.results || []) {
        const imagesResponse = await fetch(
          `${backend_url}/core/api/image/?event=${event.uuid}&page_size=100`,
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("token")}`,
            },
          },
        );
        const imagesData = await imagesResponse.json();
        const eventImages = (imagesData.results || [])
          .filter((img) => img.camera === camera.id)
          .map((img) => ({
            ...img,
            event_uuid: event.uuid,
            event_date: event.date,
          }));
        allImages = [...allImages, ...eventImages];
      }

      if (allImages.length === 0) {
        setFireCones([]);
        setShowFireCones(false);
        setLoadingCones(false);
        return;
      }

      const sortedImagesByDate = [...allImages].sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      );
      const latestImages = sortedImagesByDate.slice(0, totalFrames);
      const sortedLatestImages = [...latestImages].sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      );

      const uniqueDetections = new Map();

      for (let i = 0; i < sortedLatestImages.length; i++) {
        const img = sortedLatestImages[i];
        const frameIndex = i;

        const boxesResponse = await fetch(
          `${backend_url}/core/api/box/?image=${img.id}`,
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("token")}`,
            },
          },
        );
        const boxesData = await boxesResponse.json();

        for (const box of boxesData.results || []) {
          const specieName =
            box.specie?.name?.toLowerCase() ||
            box.specie?.id?.toLowerCase() ||
            "";
          if (specieName === "fire" || specieName === "smoke") {
            const uniqueKey = `${img.id}_${specieName}`;
            if (!uniqueDetections.has(uniqueKey)) {
              const angle = calculateAngleForFrame(
                frameIndex,
                totalFrames,
                startAngle,
                endAngle,
              );
              uniqueDetections.set(uniqueKey, {
                id: box.id,
                imageId: img.id,
                frameIndex: frameIndex,
                angle: angle,
                specie: specieName,
                imageDate: img.date,
              });
            }
          }
        }
      }

      const cones = [];
      for (const detection of Array.from(uniqueDetections.values())) {
        const { conePath, centerPoint } = calculateConeAtAngle(
          { lat: camera.latitude, lng: camera.longitude },
          detection.angle,
          coneFOV,
          coneRangeMeters,
        );
        cones.push({
          id: `${camera.id}_${detection.id}_${detection.frameIndex}`,
          angle: detection.angle,
          frameIndex: detection.frameIndex,
          path: conePath,
          centerPoint: centerPoint,
          specie: detection.specie,
          imageDate: detection.imageDate,
          cameraName: camera.description,
          cameraId: camera.id,
        });
      }

      setFireCones(cones);
      setShowFireCones(cones.length > 0);
    } catch (error) {
      console.error("Error fetching fire events:", error);
    } finally {
      setLoadingCones(false);
    }
  };

  useEffect(() => {
    const parseKMZ = async () => {
      try {
        const url = `https://firms.modaps.eosdis.nasa.gov/api/kml_fire_footprints/south_asia/24h/c6.1/FirespotArea_south_asia_c6.1_24h.kmz`;
        const response = await fetch(url);
        const kmzData = await response.blob();

        const zip = await JSZip.loadAsync(kmzData);
        const kmlFile = Object.values(zip.files).find((file) =>
          file.name.endsWith(".kml"),
        );

        if (kmlFile) {
          const kmlData = await kmlFile.async("text");
          const convertedGeoJSON = await convertXmlToGeoJSON(kmlData);

          if (convertedGeoJSON && convertedGeoJSON.features) {
            let filteredFeatures;
            if (selectedInterval !== null) {
              const [min, max] = selectedInterval.split("&&");
              filteredFeatures = convertedGeoJSON.features.filter((feature) => {
                const featureTime = extractTimestamp(
                  feature.properties.description,
                );
                if (featureTime) {
                  const currentTime = new Date();
                  const timeDifference =
                    (currentTime - featureTime) / (60 * 60 * 1000);
                  return (
                    timeDifference > parseInt(min) &&
                    timeDifference <= parseInt(max)
                  );
                }
                return false;
              });
            } else {
              filteredFeatures = convertedGeoJSON.features.filter(
                (feature) => feature,
              );
            }
            setFireFootprints({
              ...convertedGeoJSON,
              features: filteredFeatures,
            });
          } else {
            setFireFootprints(null);
          }
        } else {
          setFireFootprints(null);
        }
      } catch (error) {
        console.error("Error parsing KMZ:", error);
        setFireFootprints(null);
      }
    };
    parseKMZ();
  }, [selectedInterval]);

  const onSelect = async (item) => {
    setHoveredCone(null);
    setShowInfoWindow(true);
    setFilterType("all");
    setFireCones([]);
    setShowFireCones(false);

    if (item.user || item.isCamera) {
      item.location = { lat: item.latitude, lng: item.longitude };
      await fetchFireEventsForCamera(item);
    } else if (item.lat && item.lng) {
      item.location = { lat: item.lat, lng: item.lng };
    } else if (item.latitude && item.longitude) {
      item.location = { lat: item.latitude, lng: item.longitude };
    }

    setSelected({ theta: selected.theta, item: item });
    setCircleCenter(item.location);
    handleMarkerClick();
  };

  const handleMarkerClick = () => {
    const newRadius = 3000;
    setCircleRadius(newRadius);
    if (map) {
      const circleBounds = new window.google.maps.Circle({
        center: circleCenter,
        radius: newRadius,
      }).getBounds();
      const markerBounds = new window.google.maps.LatLngBounds();
      markerBounds.extend(circleCenter);
      map.fitBounds(markerBounds.union(circleBounds), { padding: 100 });
      const maxZoom = 18;
      if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
    }
  };

  const handleConeMouseOver = (cone) => setHoveredCone(cone);
  const handleConeMouseOut = () => setHoveredCone(null);
  const handleConeClick = (cone) => {
    setShowInfoWindow(false);
    setHoveredCone(cone);
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${backend_url}/core/api/towers/`, {
      headers: { Authorization: `Token ${localStorage.getItem("token")}` },
    })
      .then((response) => response.json())
      .then((actualData) => setData(actualData.results))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const getMarkerIcon = (description) => {
    const featureTime = extractTimestamp(description);
    if (featureTime) {
      const timeDifference = (new Date() - featureTime) / (60 * 60 * 1000);
      if (timeDifference <= 1) return "/maroon.png";
      if (timeDifference <= 3) return "/red.png";
      if (timeDifference <= 6) return "/orange.png";
      if (timeDifference <= 12) return "/yellow.png";
      return "/lime.png";
    }
    return "/marker-icon-2x.png";
  };

  const extractTimestamp = (description) => {
    const hiddenElement = document.createElement("div");
    hiddenElement.style.display = "none";
    hiddenElement.innerHTML = description;
    const detectionTimeElement = Array.from(
      hiddenElement.querySelectorAll("b"),
    ).find((element) => element.textContent.trim() === "Detection Time:");
    if (detectionTimeElement) {
      const timestampString = detectionTimeElement.nextSibling.nodeValue.trim();
      return new Date(timestampString);
    }
    return null;
  };

  const pakistanBoundsGeoJSON = {
    type: "Polygon",
    coordinates: [
      [
        [60.866374, 23.6345],
        [60.866374, 37.084107],
        [77.8375, 37.084107],
        [77.8375, 23.6345],
        [60.866374, 23.6345],
      ],
    ],
  };

  const isCoordinateInsidePakistan = (lat, lng) => {
    const point = turf.point([lng, lat]);
    const polygon = turf.polygon(pakistanBoundsGeoJSON.coordinates);
    return turf.booleanPointInPolygon(point, polygon);
  };

  const handleRadioChange = (val) => setSelectedInterval(val);
  const handleReset = () => {
    setSelectedInterval(null);
    document
      .getElementsByName("fire-interval-radio")
      .forEach((radio) => (radio.checked = false));
  };

  const filteredCones = getFilteredCones();
  const fireCount = fireCones.filter((c) => c.specie === "fire").length;
  const smokeCount = fireCones.filter((c) => c.specie === "smoke").length;

  // Determine if selected item is a camera
  const isCamera = selected.item?.user || selected.item?.isCamera;

  return (
    <div className="page">
      <div className="page__content">
        <div className="main-wrapper">
          {side_nav}
          <div style={{ display: "block", margin: 5 }}>
            <Row style={{ marginBottom: "0px", alignItems: "stretch" }}>
              <Col
                className="col-md-1 mx-0 my-0"
                style={{ width: "15.5%", maxWidth: "20.499%" }}
              >
                <Row style={{ overflowY: "scroll", height: "60vh" }}>
                  <SensorList sensorSelect={onSelect} />
                </Row>
                <Row>
                  <div style={{ paddingRight: "0px", height: "5vh" }}>
                    <a
                      href="/"
                      className="d-flex align-items-center mb-3 link-dark text-decoration-none border-bottom"
                      onClick={(e) => {
                        e.preventDefault();
                        handleReset();
                      }}
                      style={{
                        background:
                          "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
                        paddingLeft: "5px",
                      }}
                    >
                      <span
                        // className="fs-6 fw-semibold"
                        style={{ color: "#ffffff" }}
                      >
                        Map Fire Intervals
                      </span>
                      <button
                        type="button"
                        // className="btn-sm ms-auto"
                        style={{
                          marginLeft: "auto",
                          marginRight: "0px",
                          background:
                            "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
                        }}
                        onClick={() => handleReset()}
                      >
                        <FaUndo style={{ color: "#ffffff" }} />
                      </button>
                    </a>
                  </div>
                  {[
                    {
                      label: "≤1 hour",
                      value: "0&&1",
                      color: "maroon",
                      img: "/maroon.png",
                    },
                    {
                      label: "1-3 hours",
                      value: "1&&3",
                      color: "red",
                      img: "/red.png",
                    },
                    {
                      label: "3-6 hours",
                      value: "3&&6",
                      color: "orange",
                      img: "/orange.png",
                    },
                    {
                      label: "6-12 hours",
                      value: "6&&12",
                      color: "yellow",
                      img: "/yellow.png",
                    },
                    {
                      label: "12-24 hours",
                      value: "12&&24",
                      color: "lime",
                      img: "/lime.png",
                    },
                  ].map((item) => (
                    <div className="legend-item" key={item.value}>
                      <input
                        type="radio"
                        name="fire-interval-radio"
                        onChange={() => handleRadioChange(item.value)}
                      />
                      <span
                        className="legend-color"
                        style={{ background: item.color }}
                      ></span>
                      <span className="legend-text">
                        <img
                          src={item.img}
                          style={{ width: "25px" }}
                          alt={item.label}
                        />{" "}
                        {item.label}
                      </span>
                    </div>
                  ))}
                </Row>
              </Col>
              <Col style={{ paddingRight: "0rem" }}>
                <div>
                  {!isLoaded ? (
                    <h1>Loading...{loading}</h1>
                  ) : (
                    <GoogleMap
                      mapContainerStyle={mapStyles}
                      onLoad={handleMapLoad}
                      zoom={center.zoom}
                      center={center.center}
                      options={{
                        mapTypeId: "satellite",
                        streetViewControl: true,
                        scaleControl: true,
                        mapTypeControl: true,
                        rotateControl: true,
                        zoomControl: true,
                      }}
                    >
                      {/* Render Markers for Towers */}
                      {data?.map((item) => {
                        item.location = { lat: item.lat, lng: item.lng };
                        return (
                          <Marker
                            key={item.name}
                            icon="https://maps.google.com/mapfiles/ms/icons/red-dot.png"
                            position={item.location}
                            onClick={() => onSelect(item)}
                          />
                        );
                      })}

                      {/* Loading indicator */}
                      {loadingCones && (
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            background: "rgba(0,0,0,0.8)",
                            color: "white",
                            padding: "12px 24px",
                            borderRadius: "30px",
                            zIndex: 1000,
                            fontSize: "14px",
                            fontWeight: "bold",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
                          }}
                        >
                          🔍 Loading detection cones...
                        </div>
                      )}

                      {/* Render Fire/Smoke Cones */}
                      {showFireCones &&
                        filteredCones.map((cone) => {
                          const isHovered = hoveredCone?.id === cone.id;
                          const coneColor = getConeColor(
                            cone.specie,
                            isHovered,
                          );
                          return (
                            <Polygon
                              key={`fire-cone-${cone.id}`}
                              path={cone.path}
                              options={{
                                fillColor: coneColor,
                                fillOpacity: isHovered ? 0.85 : 0.65,
                                strokeColor: coneColor,
                                strokeOpacity: 1,
                                strokeWeight: isHovered ? 3 : 2,
                                geodesic: true,
                                clickable: true,
                              }}
                              onMouseOver={() => handleConeMouseOver(cone)}
                              onMouseOut={handleConeMouseOut}
                              onClick={() => handleConeClick(cone)}
                            />
                          );
                        })}

                      {/* Hover InfoWindow for Cone */}
                      {hoveredCone && hoveredCone.centerPoint && (
                        <InfoWindow
                          position={hoveredCone.centerPoint}
                          options={{
                            pixelOffset: new window.google.maps.Size(0, -10),
                            disableAutoPan: true,
                          }}
                        >
                          <div
                            style={{
                              background:
                                hoveredCone.specie === "fire"
                                  ? "linear-gradient(135deg, #FF0000 0%, #CC0000 100%)"
                                  : "linear-gradient(135deg, #FF6600 0%, #CC4400 100%)",
                              color: "white",
                              borderRadius: "12px",
                              padding: "12px 16px",
                              boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              {hoveredCone.specie === "fire" ? (
                                <FaFire size={20} />
                              ) : (
                                <FaSmog size={20} />
                              )}
                              <span
                                style={{ fontWeight: "bold", fontSize: "16px" }}
                              >
                                {hoveredCone.specie === "fire"
                                  ? "🔥 FIRE DETECTED"
                                  : "💨 SMOKE DETECTED"}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                opacity: 0.9,
                                marginBottom: "4px",
                              }}
                            >
                              📍 Camera: {hoveredCone.cameraName}
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "bold",
                                marginBottom: "4px",
                              }}
                            >
                              📐 Direction: {Math.round(hoveredCone.angle)}°
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                opacity: 0.9,
                                marginBottom: "4px",
                              }}
                            >
                              🎬 Frame: {hoveredCone.frameIndex + 1}/15
                            </div>
                            <div style={{ fontSize: "10px", opacity: 0.8 }}>
                              📅{" "}
                              {new Date(hoveredCone.imageDate).toLocaleString()}
                            </div>
                            <div
                              style={{
                                marginTop: "8px",
                                paddingTop: "6px",
                                borderTop: "1px solid rgba(255,255,255,0.3)",
                                fontSize: "10px",
                                textAlign: "center",
                              }}
                            >
                              Click to focus | Hover for details
                            </div>
                          </div>
                        </InfoWindow>
                      )}

                      {/* Render Circle around selected location */}
                      {selected.item?.location && (
                        <Circle
                          center={selected.item.location}
                          radius={circleRadius}
                          options={{
                            fillColor: "#FF0000",
                            fillOpacity: 0.08,
                            strokeColor: "#FF0000",
                            strokeOpacity: 0.6,
                            strokeWeight: 2,
                          }}
                        />
                      )}

                      {/* SINGLE InfoWindow for selected item */}
                      {selected.item?.location &&
                        !hoveredCone &&
                        showInfoWindow && (
                          <InfoWindow
                            position={selected.item.location}
                            onCloseClick={() => {
                              setSelected({ theta: selected.theta, item: {} });
                              setShowFireCones(false);
                              setFireCones([]);
                              setShowInfoWindow(true);
                            }}
                          >
                            <div
                              style={{
                                minWidth: "280px",
                                padding: "0",
                                borderRadius: "12px",
                                overflow: "hidden",
                              }}
                            >
                              {/* Header */}
                              <div
                                style={{
                                  background:
                                    "linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)",
                                  color: "white",
                                  padding: "12px 16px",
                                  fontWeight: "bold",
                                  fontSize: "14px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <FaInfoCircle size={14} />
                                {isCamera
                                  ? "Camera Information"
                                  : "Location Information"}
                              </div>

                              {/* Body */}
                              <div style={{ padding: "12px 16px" }}>
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "15px",
                                    marginBottom: "8px",
                                    color:
                                      "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
                                  }}
                                >
                                  📍{" "}
                                  {selected.item.user
                                    ? selected.item.description
                                    : selected.item.name ||
                                      selected.item.description}
                                </div>

                                <div
                                  style={{
                                    marginBottom: "12px",
                                    fontSize: "12px",
                                    color: "#666",
                                  }}
                                >
                                  📌 Lat:{" "}
                                  {selected.item.location.lat.toFixed(6)}, Lng:{" "}
                                  {selected.item.location.lng.toFixed(6)}
                                </div>

                                {/* Filter buttons - only for cameras */}
                                {isCamera && fireCones.length > 0 && (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      marginBottom: "12px",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <button
                                      onClick={() => setFilterType("all")}
                                      style={{
                                        flex: 1,
                                        padding: "6px 8px",
                                        background:
                                          filterType === "all"
                                            ? "#007bff"
                                            : "#e0e0e0",
                                        color:
                                          filterType === "all"
                                            ? "white"
                                            : "#333",
                                        border: "none",
                                        borderRadius: "20px",
                                        cursor: "pointer",
                                        fontSize: "11px",
                                        fontWeight: "bold",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <FaEye size={10} /> All (
                                      {fireCones.length})
                                    </button>
                                    <button
                                      onClick={() => setFilterType("fire")}
                                      style={{
                                        flex: 1,
                                        padding: "6px 8px",
                                        background:
                                          filterType === "fire"
                                            ? "#dc3545"
                                            : "#e0e0e0",
                                        color:
                                          filterType === "fire"
                                            ? "white"
                                            : "#333",
                                        border: "none",
                                        borderRadius: "20px",
                                        cursor: "pointer",
                                        fontSize: "11px",
                                        fontWeight: "bold",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <FaFire size={10} /> Fire ({fireCount})
                                    </button>
                                    <button
                                      onClick={() => setFilterType("smoke")}
                                      style={{
                                        flex: 1,
                                        padding: "6px 8px",
                                        background:
                                          filterType === "smoke"
                                            ? "#fd7e14"
                                            : "#e0e0e0",
                                        color:
                                          filterType === "smoke"
                                            ? "white"
                                            : "#333",
                                        border: "none",
                                        borderRadius: "20px",
                                        cursor: "pointer",
                                        fontSize: "11px",
                                        fontWeight: "bold",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <FaSmog size={10} /> Smoke ({smokeCount})
                                    </button>
                                    <button
                                      onClick={() => setFilterType("none")}
                                      style={{
                                        flex: 1,
                                        padding: "6px 8px",
                                        background:
                                          filterType === "none"
                                            ? "#6c757d"
                                            : "#e0e0e0",
                                        color:
                                          filterType === "none"
                                            ? "white"
                                            : "#333",
                                        border: "none",
                                        borderRadius: "20px",
                                        cursor: "pointer",
                                        fontSize: "11px",
                                        fontWeight: "bold",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <FaEyeSlash size={10} /> Hide All
                                    </button>
                                  </div>
                                )}

                                {/* Detection summary - only for cameras */}
                                {isCamera &&
                                  filteredCones.length > 0 &&
                                  filterType !== "none" && (
                                    <div
                                      style={{
                                        marginBottom: "12px",
                                        padding: "8px",
                                        background: "#fff3cd",
                                        borderRadius: "8px",
                                        textAlign: "center",
                                      }}
                                    >
                                      <strong style={{ color: "#856404" }}>
                                        🔥 Showing {filteredCones.length}{" "}
                                        detection(s)
                                      </strong>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          marginTop: "6px",
                                        }}
                                      >
                                        {filteredCones
                                          .slice(0, 5)
                                          .map((cone, idx) => (
                                            <span
                                              key={idx}
                                              style={{
                                                display: "inline-block",
                                                margin: "2px",
                                                padding: "2px 8px",
                                                background:
                                                  cone.specie === "fire"
                                                    ? "#dc3545"
                                                    : "#fd7e14",
                                                color: "white",
                                                borderRadius: "20px",
                                                fontSize: "10px",
                                                fontWeight: "bold",
                                              }}
                                            >
                                              {cone.specie === "fire"
                                                ? "🔥"
                                                : "💨"}{" "}
                                              {Math.round(cone.angle)}°
                                            </span>
                                          ))}
                                        {filteredCones.length > 5 && (
                                          <span
                                            style={{
                                              fontSize: "10px",
                                              color: "#666",
                                              marginLeft: "4px",
                                            }}
                                          >
                                            +{filteredCones.length - 5} more
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                {isCamera && filterType === "none" && (
                                  <div
                                    style={{
                                      marginBottom: "12px",
                                      padding: "8px",
                                      background: "#e0e0e0",
                                      borderRadius: "8px",
                                      textAlign: "center",
                                      color: "#666",
                                    }}
                                  >
                                    👁️ All cones hidden. Select a filter to
                                    show.
                                  </div>
                                )}

                                {/* Zoom Controls */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                    marginBottom: "10px",
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      setCenter({
                                        center: selected.item.location,
                                        zoom: (center.zoom || 10) + 2,
                                        isZoom: true,
                                      })
                                    }
                                    style={{
                                      flex: 1,
                                      padding: "6px 12px",
                                      background: "#007bff",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "20px",
                                      cursor: "pointer",
                                      fontSize: "11px",
                                      fontWeight: "bold",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <AiOutlineZoomIn size={12} /> Zoom In
                                  </button>
                                  <button
                                    onClick={() =>
                                      setCenter({
                                        center: {
                                          lat: 34.19167584003779,
                                          lng: 73.23095807365446,
                                        },
                                        zoom: 9,
                                        isZoom: false,
                                      })
                                    }
                                    style={{
                                      flex: 1,
                                      padding: "6px 12px",
                                      background: "#6c757d",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "20px",
                                      cursor: "pointer",
                                      fontSize: "11px",
                                      fontWeight: "bold",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <AiOutlineZoomOut size={12} /> Reset
                                  </button>
                                </div>

                                {/* Live View Button - for cameras only */}
                                {isCamera && selected.item.id && (
                                  <Link
                                    to={`/live/${selected.item.id}`}
                                    target="_blank"
                                    style={{
                                      display: "block",
                                      width: "100%",
                                      padding: "8px",
                                      background:
                                        "linear-gradient(135deg, #28a745 0%, #1e7e34 100%)",
                                      color: "white",
                                      textDecoration: "none",
                                      borderRadius: "20px",
                                      fontSize: "12px",
                                      textAlign: "center",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    🎥 Live View
                                  </Link>
                                )}

                                {/* Dashboard Link - for sensors/towers */}
                                {!isCamera &&
                                  selected.item.things_board_link && (
                                    <a
                                      href={selected.item.things_board_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        display: "block",
                                        width: "100%",
                                        padding: "8px",
                                        background:
                                          "linear-gradient(135deg, #17a2b8 0%, #138496 100%)",
                                        color: "white",
                                        textDecoration: "none",
                                        borderRadius: "20px",
                                        fontSize: "12px",
                                        textAlign: "center",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      📊 Dashboard
                                    </a>
                                  )}
                              </div>
                            </div>
                          </InfoWindow>
                        )}

                      {/* Render Fire Footprints - only if fireFootprints exists */}
                      {fireFootprints &&
                        fireFootprints.features &&
                        fireFootprints.features.map((feature, idx) => {
                          if (feature.geometry.type === "Polygon") {
                            const isInsidePakistan =
                              feature.geometry.coordinates[0].some(
                                ([lng, lat]) =>
                                  isCoordinateInsidePakistan(lat, lng),
                              );
                            if (isInsidePakistan) {
                              return (
                                <Polygon
                                  key={idx}
                                  path={feature.geometry.coordinates[0].map(
                                    ([lng, lat]) => ({ lat, lng }),
                                  )}
                                  options={{
                                    fillColor: "#FF0000",
                                    fillOpacity: 0.35,
                                    strokeColor: "#FFFFFF",
                                    strokeOpacity: 0.7,
                                    strokeWeight: 1.5,
                                  }}
                                />
                              );
                            }
                          } else if (feature.geometry.type === "Point") {
                            if (
                              isCoordinateInsidePakistan(
                                feature.geometry.coordinates[1],
                                feature.geometry.coordinates[0],
                              )
                            ) {
                              return (
                                <Marker
                                  key={idx}
                                  position={{
                                    lat: feature.geometry.coordinates[1],
                                    lng: feature.geometry.coordinates[0],
                                  }}
                                  icon={{
                                    url: getMarkerIcon(
                                      feature.properties.description,
                                    ),
                                    scaledSize: new window.google.maps.Size(
                                      22,
                                      22,
                                    ),
                                  }}
                                  onClick={() => setSelectedFeature(feature)}
                                />
                              );
                            }
                          }
                          return null;
                        })}

                      {selectedFeature && (
                        <InfoWindow
                          position={{
                            lat: selectedFeature.geometry.coordinates[1],
                            lng: selectedFeature.geometry.coordinates[0],
                          }}
                          onCloseClick={() => setSelectedFeature(null)}
                        >
                          <div style={{ maxWidth: "250px", padding: "8px" }}>
                            <h6 style={{ margin: "0 0 5px 0", color: "#333" }}>
                              {selectedFeature.properties.name}
                            </h6>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "11px",
                                color: "#666",
                              }}
                              dangerouslySetInnerHTML={{
                                __html: selectedFeature.properties.description,
                              }}
                            />
                          </div>
                        </InfoWindow>
                      )}
                    </GoogleMap>
                  )}
                </div>
              </Col>
              <Col
                style={{
                  width: "15.5%",
                  padding: "0 0rem",
                  flex: "0 0 .499%",
                  maxWidth: "30.499%",
                }}
              >
                <EventList
                  eventClick={(item) =>
                    setSelected({ theta: selected.theta, item: item })
                  }
                />
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MapContainer;
